import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { SiteTakeoverCampaignUSP } from './SiteTakeoverCampaignUSP'
import BodyText from 'LuxKit/Typography/BodyText'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import { GenericBackgroundImage } from './GenericBackgroundImage'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import { useSiteTakeoverCampaignUIState } from '../hooks/useSiteTakeoverCampaignUIState'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { getThemeClassName } from 'lib/theme/themeUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useIntl } from 'react-intl'

const Header = styled.div`
  position: relative;
  overflow: hidden;
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} ${rem(20)};
  }
`

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  z-index: -1;
`

const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: ${rem(32)} 1fr;
  grid-template-areas:
    'logo title'
    'subtitle subtitle';
  column-gap: ${rem(8)};
  row-gap: 0;
  align-items: start;

  > :nth-child(1) {
    grid-area: logo;
    width: ${rem(32)};
    height: ${rem(32)};
    min-width: ${rem(32)};
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
  }

  > :nth-child(1) img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  > :nth-child(2) {
    grid-area: title;
    margin: 0;
    align-self: center;
  }

  > :nth-child(3) {
    grid-area: subtitle;
    margin: 0;
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(60)} 1fr;
    grid-template-areas:
      'logo title'
      'logo subtitle';
    column-gap: ${rem(16)};
    row-gap: 0;

    > :nth-child(1) {
      width: ${rem(60)};
      height: ${rem(60)};
      min-width: ${rem(60)};
      align-self: start;
    }
  }
`

const BodyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding-block: ${rem(20)} 0;
  padding-inline: ${rem(20)};
`

function SiteTakeoverCampaignModal() {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const siteTakeoverTheme = useSiteTakeoverTheme()

  const { modalPayload, targetedCampaignId } = useSiteTakeoverCampaignUIState()
  const appDispatch = useAppDispatch()

  const handleDismiss = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const takeoverBackground = useMemo(() => {
    const mobileRef = siteTakeoverTheme?.mobileBackgroundRef ?? siteTakeoverTheme?.backgroundRef
    const desktopRef = siteTakeoverTheme?.backgroundRef

    if (!mobileRef && !desktopRef) {
      return null
    }

    if (mobileRef && mobileRef !== desktopRef) {
      return <SiteTakeoverBackground>
        <CSSBreakpoint max="mobile">
          <GenericBackgroundImage imageId={mobileRef} backgroundStyle={siteTakeoverTheme?.labelStyle} />
        </CSSBreakpoint>
        {desktopRef && <CSSBreakpoint min="tablet">
          <GenericBackgroundImage imageId={desktopRef} backgroundStyle={siteTakeoverTheme?.labelStyle} />
        </CSSBreakpoint>}
      </SiteTakeoverBackground>
    }

    return <SiteTakeoverBackground>
      <GenericBackgroundImage imageId={desktopRef!} backgroundStyle={siteTakeoverTheme?.labelStyle} />
    </SiteTakeoverBackground>
  }, [siteTakeoverTheme])

  const headerPalette = siteTakeoverTheme?.labelPalette ?? 'default'
  const headerPaletteClassName = useMemo(() => getThemeClassName({ mobile: headerPalette }), [headerPalette])

  const headerTitle = useMemo(() => {
    return <>
      <Heading className={headerPaletteClassName} variant="heading3">{modalPayload?.title}</Heading>
      <BodyText className={headerPaletteClassName} variant="medium">{modalPayload?.subtitle}</BodyText>
    </>
  }, [modalPayload, headerPaletteClassName])

  const handlePrimaryAction = useCallback(() => {
    if (modalPayload?.ctaLink) {
      appDispatch(pushWithRegion(modalPayload.ctaLink))
    }
    modalElement.resolve()
  }, [modalPayload, appDispatch, modalElement])

  const viewAllOffersLabel = intl.formatMessage({
    id: 'E5uP_k5dxsFbQ9idg5BS',
    defaultMessage: 'View all offers',
  })

  const primaryActionProps = useMemo(() => ({
    children: viewAllOffersLabel,
    onClick: handlePrimaryAction,
  }), [handlePrimaryAction, viewAllOffersLabel])

  const closeLabel = intl.formatMessage({
    id: 'Gcvg0yISL4EmtT-FgwV3',
    defaultMessage: 'Close',
  })

  const secondaryActionProps = useMemo(() => ({
    children: closeLabel,
    onClick: handleDismiss,
  }), [handleDismiss, closeLabel])

  if (!modalPayload) {
    return null
  }

  return <ModalBase
    dismissible
    onClose={handleDismiss}
  >
    <Header>
      {takeoverBackground}
      {siteTakeoverTheme?.logoRef && <HeaderContent>
        <Image image={{ id: siteTakeoverTheme.logoRef }} />
        {headerTitle}
      </HeaderContent>}
      {!siteTakeoverTheme?.logoRef && headerTitle}
    </Header>
    <ModalBody>
      <BodyContent>
        {'descriptionBeforeUSP' in modalPayload && (
          <MarkdownRender fontSize="medium" content={modalPayload.descriptionBeforeUSP} />
        )}
        {/* Only render USP if we have a campaign ID */}
        {targetedCampaignId && (
          <SiteTakeoverCampaignUSP campaignTagId={targetedCampaignId} />
        )}
        {'descriptionAfterUSP' in modalPayload && (
          <MarkdownRender fontSize="medium" content={modalPayload.descriptionAfterUSP} />
        )}
      </BodyContent>
    </ModalBody>
    <ModalFooter
      actionsLayout="stacked"
      tabletActionsLayout="auto"
      primaryActionProps={primaryActionProps}
      secondaryActionProps={secondaryActionProps}
    />
  </ModalBase>
}

export default SiteTakeoverCampaignModal
